import $ from 'jquery';
import './accdc/Acc.DC.API.U';
import './accdc/modules/calendar_generator';
import Class from './DatePickerClasses';
import DatePickerComponent from './DatePickerComponent';
import {
  constrainDate,
  previousDay,
  nextDay,
  previousWeek,
  nextWeek,
  previousMonth,
  nextMonth,
  getISODateString,
} from '../date/DateHelpers';
import SvgIcon from '../../../utils/SvgIcon';
import IconSprites from '../../../utils/IconSprites';

const RangeType = {
  NONE: 0,
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
};

const RangeDateModifiers = {
  [RangeType.MONTH]: {
    increment: nextMonth,
    decrement: previousMonth,
  },
  [RangeType.DAY]: {
    increment: nextDay,
    decrement: previousDay,
  },
  [RangeType.WEEK]: {
    increment: nextWeek,
    decrement: previousWeek,
  },
};

const defaults = {
  date: null, // Initial selected date, defaults to new Date()
  minDate: null, // Earliest selectable date
  maxDate: null, // Latest selectable date
  rangeType: RangeType.NONE, //
  dateFormatter: null, // fn to format selected display date
  id: 'tn-date-picker', // Used to hook up label<->input & id the whatsock calendar instance
  label: 'Select Date', // Input label
  onChange: null,
  container: null, // element/selector string, will auto-prepend() if specified
  calendarClassName: 'tn-datepicker',
};

function makeEl({ id, label, rangeType }) {
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;

  const html = `<div class="${Class.BASE} ${rangeType ? `${Class.BASE}--with-range-btns` : ''}" style="display: none;">
      <p id="${labelId}" class="sr-only">${label}</p>

      <div class="${Class.BASE}__btn-container">
        <button aria-labelledby="${labelId}" aria-describedby="${descriptionId}" type="button" class="btn btn-default btn-lg ${Class.BASE}__btn">
          <span id="${descriptionId}" class="${Class.BASE}__date-display"></span>
          ${SvgIcon.getHtml(IconSprites.Calendar, `${Class.BASE}__icon-calendar`)}
        </button>

      ${rangeType ? `
        <button type="button" class="btn btn-lg btn-primary ${Class.BTN_PERIOD} ${Class.BTN_PERIOD_PREV}">
          ${SvgIcon.getHtml(IconSprites.ChevronLeft, `${Class.BASE}__icon-chevron-left`)}
          <span class="sr-only">Previous ${rangeType}</span>
        </button>

        <button type="button" class="btn btn-lg btn-primary ${Class.BTN_PERIOD} ${Class.BTN_PERIOD_NEXT}">
        ${SvgIcon.getHtml(IconSprites.ChevronRight, `${Class.BASE}__icon-chevron-right`)}
          <span class="sr-only">Next ${rangeType}</span>
        </button>` : ''}

      </div>
    </div>`;

  return $(html);
}

class DatePicker {
  constructor(options_ = {}) {
    const options = { ...defaults, ...options_ };
    this.date = options.date || new Date();

    this.minDate = options.minDate;
    this.maxDate = options.maxDate;
    this.rangeType = options.rangeType;
    this.dateFormatter = options.dateFormatter;
    this.onChange = options.onChange;

    this.$el = makeEl(options);
    this.$input = document.createElement('input');
    this.$btn = this.find(`.${Class.BASE}__btn`);
    this.$dateDisplay = this.find(`.${Class.BASE}__date-display`);

    const { container, id, calendarClassName } = options;

    if (container) {
      $(container).prepend(this.$el);
    }

    this.initRangeBtns();
    this.initDatepicker(id, calendarClassName);

    this.setDate(this.date);

    this.$el.show();
  }

  onUpdate(date) {
    this.setDate(date);
    this.triggerChange();
  }

  updateInputValue() {
    this.$input.value = `${getISODateString(this.date)}T00:00:00`;
  }

  updateSelectedDateDisplay() {
    this.$dateDisplay.text(this.dateFormatter(this.date));
  }

  initRangeBtns() {
    const { rangeType } = this;

    if (rangeType !== RangeType.NONE) {
      this.$btnPrev = this.find(`.${Class.BTN_PERIOD_PREV}`);
      this.$btnNext = this.find(`.${Class.BTN_PERIOD_NEXT}`);

      this.$btnPrev.on('click', () => {
        const date = RangeDateModifiers[rangeType].decrement(this.date);
        this.setDate(date);
        this.triggerChange();
      });

      this.$btnNext.on('click', () => {
        const date = RangeDateModifiers[rangeType].increment(this.date);
        this.setDate(date);
        this.triggerChange();
      });
    }
  }

  updateRangeBtnsState() {
    if (this.rangeType !== RangeType.NONE) {
      const dateTime = this.date.getTime();
      // TODO additionally check to see if the range encompasses
      // the entire span of time from minDate to maxDate.

      if (this.minDate) {
        this.$btnPrev.attr('disabled', this.minDate.getTime() === dateTime);
      }
      if (this.maxDate) {
        this.$btnNext.attr('disabled', this.maxDate.getTime() === dateTime);
      }
    }
  }

  triggerChange() {
    const { onChange } = this;
    if (onChange) {
      onChange(this.date, this);
    }
  }

  getDate() {
    return this.date;
  }

  setDate(value) {
    this.date = this.minMaxDate(value);
    this.updateInputValue();
    this.updateSelectedDateDisplay();
    this.updateRangeBtnsState();
  }

  minMaxDate(value) {
    return constrainDate(value, this.minDate, this.maxDate);
  }

  setDates({ date, minDate, maxDate }) {
    this.minDate = minDate;
    this.maxDate = maxDate;
    this.setDate(date);
  }

  setMaxDate(value) {
    this.maxDate = value;

    const prevDateTime = this.date.getTime();
    const newDate = this.minMaxDate(this.date);

    if (newDate.getTime() !== prevDateTime) {
      this.setDate(newDate);
      this.triggerChange();
    }
  }

  setMinDate(value) {
    this.minDate = value;

    const prevDateTime = this.date.getTime();
    const newDate = this.minMaxDate(this.date);

    if (newDate.getTime() !== prevDateTime) {
      this.setDate(newDate);
      this.triggerChange();
    }
  }

  find(selector) {
    return this.$el.find(selector);
  }

  initDatepicker(id, className) {
    /* eslint-disable no-param-reassign */
    // http://whatsock.com/tsg/Coding%20Arena/ARIA%20Date%20Pickers/ARIA%20Date%20Picker%20(with%20Disabled%20Date%20Ranges)/demo.htm
    // http://whatsock.com/tsg/Coding%20Arena/ARIA%20Date%20Pickers/ARIA%20Date%20Picker%20(with%20Disabled%20Date%20Ranges)/js/setup.js

    const { $A, tnew } = window;

    const afterClose = (dc) => {
      dc.firstResetDate = false;
    };

    const beforeOpen = (dc) => {
      if (!dc.firstResetDate) {
        // Store a variable in the dc object to ensure this only runs when the date picker
        // first opens, and not every time such as when switching between months or years
        dc.firstResetDate = true;

        // Make the datepicker's date and 'current' object match our date
        dc.date = new Date(this.date.getTime());
        dc.setCurrent(dc);
        dc.fn.current = {};
        Object.assign(dc.fn.current, dc.range.current);
      }

      const dateObject = (date) => ({
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
      });

      let disableAllDays = false;
      const toDisable = [];

      if (this.minDate) {
        const { year: minYear, month: minMonth, day: minDay } = dateObject(this.minDate);
        const currentRange = dc.range.current;

        // Disable all days if we're in a month that comes before minDate
        if (
          minYear > currentRange.year
          || (minYear === currentRange.year && minMonth > currentRange.month)
        ) {
          disableAllDays = true;
        } else {
          // Disable all days prior to minDate if we're in the same month as minDate
          if (minYear === currentRange.year && minMonth === currentRange.month) {
            for (let i = 1; i < minDay; i += 1) {
              toDisable.push(i);
            }
          }
        }
      }

      // Do the same as above but reverse for maxDate
      if (this.maxDate && !disableAllDays) {
        const { year: maxYear, month: maxMonth, day: maxDay } = dateObject(this.maxDate);
        const currentRange = dc.range.current;

        if (
          maxYear < currentRange.year
          || (maxYear === currentRange.year && maxMonth < currentRange.month)
        ) {
          disableAllDays = true;
        } else {
          if (maxYear === currentRange.year && maxMonth === currentRange.month) {
            for (let i = maxDay + 1; i <= 31; i += 1) {
              toDisable.push(i);
            }
          }
        }
      }

      if (disableAllDays) {
        const allDays = Array.from(new Array(31), (val, index) => index + 1); // [1, 2, 3, ..., 31]
        toDisable.push(...allDays);
      }

      dc.range[dc.range.current.month].disabled[dc.range.current.year] = toDisable;
    };

    const helpTextId = `${id}-help-text`;
    const helpTextShortContent = DatePickerComponent.helpText.replaceAll('{:HelpKeyCommand}', '<kbd>F1</kbd>');
    const helpTextShort = `<span id="${helpTextId}" aria-label="For keyboard shortcuts, press F1.">${helpTextShortContent}</span>`;

    $A.setCalendar(id, this.$btn[0], this.$input, false, (event, dc) => {
      const { year, month, mDay: day } = dc.range.current;
      const date = new Date(year, month, day);

      this.onUpdate(date);

      dc.close();
    }, {
      className,
      leftButtonMonthText: SvgIcon.getHtml(IconSprites.ChevronLeft),
      leftButtonYearText: SvgIcon.getHtml(IconSprites.ChevronLeft),
      rightButtonMonthText: SvgIcon.getHtml(IconSprites.ChevronRight),
      rightButtonYearText: SvgIcon.getHtml(IconSprites.ChevronRight),
      autoPosition: 0,
      helpTextShort,
      describedById: helpTextId,
      helpText: `
      <ul class="list-unstyled tn-datepicker__key-command-list">
        <li>${DatePickerComponent.navigateByDayLabel}<br><kbd>↑</kbd> <kbd>↓</kbd> <kbd>←</kbd> <kbd>→</kbd></li>
        <li>${DatePickerComponent.navigateByMonthLabel}<br><kbd>PageUp</kbd>, <kbd>PageDown</kbd></li>
        <li>${DatePickerComponent.navigateByYearLabel}<br><kbd>Alt</kbd>+<kbd>PageUp</kbd>, <kbd>Alt</kbd>+<kbd>PageDown</kbd></li>
        <li>${DatePickerComponent.cancelLabel}<br><kbd>Esc</kbd></li>
      </ul>`,
      wdOffset: tnew.app.calendarViewConfig.firstDayOfWeek,
      months: tnew.app.calendarViewConfig?.monthsLabel,
      days: tnew.app.calendarViewConfig?.weekdaysLabel.map(weekday => {
        return {
          s: weekday[0],
          l: weekday
        }
      }),
    });

    const picker = $A.reg[id];
    const { runBefore, close } = picker;

    picker.runBefore = () => {
      beforeOpen(picker);
      runBefore(picker);
    };
    picker.close = () => {
      afterClose(picker);
      close(picker);
    };
    /* eslint-enable no-param-reassign */
  }
}

DatePicker.RangeType = RangeType;
DatePicker.defaults = defaults;

export default DatePicker;
